import axios, { AxiosInstance, AxiosError, AxiosResponse } from 'axios';
import {
  CreateRoomParams,
  LoginInMeeting,
  LoginInMeetingResponse,
  MeetingRoom,
  CreateEditMeetingRoom,
  RoomConnectParams,
  SendMessage,
  GetMessages,
  GetMessagesParams,
  DocProps
} from '@/types/api.ts';
import { showToast } from '@/components/ToastManager';

class Api {
  private axiosInstance: AxiosInstance;

  constructor() {
    this.axiosInstance = axios.create({
      baseURL: import.meta.env.VITE_API_BASE_URL || '/api/v1',
      withCredentials: true,
      headers: {
        'Content-Type': 'application/json'
      }
    });

    this.axiosInstance.interceptors.response.use(
      (response: AxiosResponse) => response,
      (error: AxiosError) => {
        showToast('errorToast', 'error', 'Ошибка запроса', error.message);
        return Promise.reject(error);
      }
    );
  }

  public async loginInMeeting({
    meetingUid,
    userUid,
    assistantInviteToken
  }: LoginInMeeting): Promise<LoginInMeetingResponse> {
    try {
      const response = await this.axiosInstance.post('/participants/login-in-meeting', {
        meeting_uid: meetingUid,
        user_uid: userUid,
        assistant_invite_token: assistantInviteToken
      });
      return response.data;
    } catch (error) {
      console.error('Error login in meeting:', error);
      throw new Error('Error login in meeting');
    }
  }

  public async generateToken({ meetingUid, identity, name }: RoomConnectParams): Promise<string> {
    try {
      const { data } = await this.axiosInstance.post('/rooms/room-token', {
        room_uid: meetingUid,
        identity,
        name
      });
      return data.token;
    } catch (error) {
      console.error('Error generating token:', error);
      throw new Error('Error generating token');
    }
  }

  public async createMeeting(params: CreateRoomParams) {
    try {
      const { data } = await this.axiosInstance.post('/meeting/', params);
      return data;
    } catch (error) {
      console.error('Error creating meeting:', error);
      throw new Error('Error creating meeting');
    }
  }

  public async getMeeting(meetingUid: string) {
    try {
      const { data } = await this.axiosInstance.get(`/meeting/${meetingUid}`);
      return data;
    } catch (error) {
      console.error('Error getting meeting:', error);
      throw new Error('Error getting meeting');
    }
  }

  public async getCurrentMeeting(meetingUid: string) {
    try {
      const { data } = await this.axiosInstance.get(`/meeting/${meetingUid}/current`);
      return data;
    } catch (error) {
      console.error('Error getting current meeting:', error);
      throw new Error('Error getting current meeting');
    }
  }

  public async getStudyMode(meetingUid: string): Promise<boolean> {
    try {
      const { data } = await this.axiosInstance.get(`/meeting/${meetingUid}/study-mode`);
      return data;
    } catch (error) {
      console.error('Error get study mode:', error);
      throw new Error('Error get study mode');
    }
  }

  public async changeStudyMode(meetingUid: string): Promise<boolean> {
    try {
      const { data } = await this.axiosInstance.put(`/meeting/${meetingUid}/change-study-mode`);
      return data;
    } catch (error) {
      console.error('Error switching study mode:', error);
      throw new Error('Error switching study mode');
    }
  }

  public async authorDisconnect(meetingUid: string) {
    try {
      const { data } = await this.axiosInstance.put(`/meeting/${meetingUid}/finish`);
      return data;
    } catch (error) {
      console.error('Error disconnect from meeting:', error);
      throw new Error('Error disconnect from meeting');
    }
  }

  public async checkRoom(meetingUid: string) {
    try {
      const { data } = await this.axiosInstance.get(`/meeting/${meetingUid}/check`);
      return data;
    } catch (error) {
      console.error('Error checking room:', error);
      throw new Error('Error checking room');
    }
  }

  public async uploadMeetingPdf(meetingUid: string, file: File): Promise<DocProps> {
    try {
      const formData = new FormData();
      formData.append('file', file);

      const { data } = await this.axiosInstance.post(
        `/files/meeting/${meetingUid}/upload-file`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      return data;
    } catch (error) {
      console.error('Error uploading file:', error);
      throw new Error('Error uploading file');
    }
  }

  public async uploadMeetingDrawingPdf(meetingUid: string, file: File): Promise<DocProps> {
    try {
      const formData = new FormData();
      formData.append('file', file);

      const { data } = await this.axiosInstance.post(
        `/files/meeting/${meetingUid}/upload-drawing-file`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      return data;
    } catch (error) {
      console.error('Error uploading drawing file:', error);
      throw new Error('Error uploading drawing file');
    }
  }

  public async uploadBoard(meetingUid: string, userUid: string, image: Blob, pageIndex?: string) {
    try {
      const formData = new FormData();
      formData.append('meeting_uuid', meetingUid);
      formData.append('user_uuid', userUid);
      const filename = pageIndex
        ? `board_${userUid}_page_${pageIndex}.png`
        : `board_${userUid}.png`;
      formData.append('file', image, filename);

      await this.axiosInstance.post('/boards/', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
    } catch (error) {
      console.error('Error uploading board:', error);
      throw new Error('Error uploading board');
    }
  }

  public uploadBoardBeacon(
    meetingUid: string,
    userUid: string,
    image: Blob,
    pageIndex?: string
  ): boolean {
    const formData = new FormData();
    formData.append('meeting_uuid', meetingUid);
    formData.append('user_uuid', userUid);
    const filename = pageIndex ? `board_${userUid}_page_${pageIndex}.png` : `board_${userUid}.png`;
    formData.append('file', image, filename);

    return navigator.sendBeacon(`${this.axiosInstance.defaults.baseURL}/boards/`, formData);
  }

  public async getCurrentFileForMeeting(meetingUid: string): Promise<string> {
    try {
      const { data } = await this.axiosInstance.get(`/files/meeting/${meetingUid}/current-file`);

      return data;
    } catch (error) {
      console.error('Error getting the file:', error);
      throw new Error('Error getting the file');
    }
  }

  public async getActiveFileForMeeting(meetingUid: string): Promise<DocProps> {
    try {
      const { data } = await this.axiosInstance.get(`/files/meeting/${meetingUid}/active-file`);

      return data;
    } catch (error) {
      console.error('Error getting the active file:', error);
      throw new Error('Error getting the active file');
    }
  }

  public async getAllFiles(meetingUid: string): Promise<string[]> {
    try {
      const { data } = await this.axiosInstance.get(`/files/meeting/${meetingUid}/files`);

      return data;
    } catch (error) {
      console.error('Error getting files:', error);
      throw new Error('Error getting files');
    }
  }

  public async setActiveFile(meetingUid: string, fileId: string): Promise<boolean> {
    try {
      const { data } = await this.axiosInstance.post(
        `/files/meeting/${meetingUid}/active-file/${fileId}`
      );
      return data;
    } catch (error) {
      console.error('Error setting active file:', error);
      throw new Error('Error setting active file');
    }
  }

  public async setUnActiveFile(meetingUid: string): Promise<boolean> {
    try {
      const { data } = await this.axiosInstance.post(`/files/meeting/${meetingUid}/unactive-file`);
      return data;
    } catch (error) {
      console.error('Error setting unactive file:', error);
      throw new Error('Error setting unactive file');
    }
  }

  public async getUploadFiles(meetingUid: string): Promise<DocProps[]> {
    try {
      const { data } = await this.axiosInstance.get(`/files/meeting/${meetingUid}/upload-files`);
      return data;
    } catch (error) {
      console.error('Error getting upload files', error);
      throw new Error('Error getting upload files');
    }
  }

  public async getStudentFiles(meetingUid: string): Promise<DocProps[]> {
    try {
      const { data } = await this.axiosInstance.get(`/files/meeting/${meetingUid}/student-files`);
      return data;
    } catch (error) {
      console.error('Error getting student files', error);
      throw new Error('Error getting student files');
    }
  }

  public async shareFileWithStudentInMeeting(meetingUid: string, fileId: string): Promise<boolean> {
    try {
      const { data } = await this.axiosInstance.post(
        `/files/meeting/${meetingUid}/share-with-students/${fileId}`
      );
      return data;
    } catch {
      console.error('Error sharing file with students');
      throw new Error('Error sharing file with students');
    }
  }

  public async unshareFileWithStudentInMeeting(
    meetingUid: string,
    fileId: string
  ): Promise<boolean> {
    try {
      const { data } = await this.axiosInstance.post(
        `/files/meeting/${meetingUid}/unshare-with-students/${fileId}`
      );
      return data;
    } catch {
      console.error('Error unsharing file with students');
      throw new Error('Error unsharing file with students');
    }
  }

  public async deleteFileFromMeeting(meetingUid: string, fileId: string): Promise<boolean> {
    try {
      const { data } = await this.axiosInstance.delete(`/files/meeting/${meetingUid}/${fileId}`);
      return data;
    } catch (error) {
      console.error('Error deleting file from meeting', error);
      throw new Error('Error deleting file from meeting');
    }
  }

  public async getTeacherBoards(meetingUid: string): Promise<string[]> {
    try {
      const { data } = await this.axiosInstance.get(`/boards/teacher-boards/${meetingUid}`);

      return data;
    } catch (error) {
      console.error('Error getting teacher boards:', error);
      throw new Error('Error getting teacher boards');
    }
  }

  public async getZipWithBoards(meetingUid: string, userUid: string) {
    try {
      const data = await this.axiosInstance.get(`/boards/download-zip/${meetingUid}/${userUid}`, {
        responseType: 'blob'
      });

      if (data.status === 204) {
        return null;
      }

      return data;
    } catch (error) {
      console.error('Error getting url for download zip with boards:', error);
      throw new Error('Error getting url for download zip with boards');
    }
  }

  public async addParticipant(meetingUid: string, userUid: string, userName: string) {
    try {
      const { data } = await this.axiosInstance.post(
        `/participants/meeting/${meetingUid}/add-participant`,
        { user_uuid: userUid, name: userName }
      );
      return data;
    } catch (error) {
      console.error('Error adding participant:', error);
      throw new Error('Error adding participant');
    }
  }

  public async disconnectParticipant(meetingUid: string, userUid: string): Promise<boolean> {
    try {
      const { data } = await this.axiosInstance.put(
        `/participants/meeting/${meetingUid}/disconnect-participant`,
        { user_uuid: userUid }
      );
      return data;
    } catch (error) {
      console.error('Error disconnecting participant:', error);
      throw new Error('Error disconnecting participant');
    }
  }

  public async getNameParticipant(meetingUid: string, userUid: string): Promise<string> {
    try {
      const { data } = await this.axiosInstance.post(
        `/participants/meeting/${meetingUid}/get-name-participant`,
        { user_uuid: userUid }
      );
      return data;
    } catch (error) {
      console.error('Error getting name of participant:', error);
      throw new Error('Error getting name of participant');
    }
  }

  public async getLinkTokenAssistant(meetingUid: string): Promise<string | null> {
    try {
      const { data } = await this.axiosInstance.get(
        `/meeting/${meetingUid}/get-invite-token-to-assistant`
      );
      return data;
    } catch (error) {
      console.error('Error getting token to invite assistant:', error);
      throw new Error('Error getting token to invite assistant');
    }
  }

  public async getMeetingByAssistant(assistantInviteToken: string): Promise<string | null> {
    try {
      const { data } = await this.axiosInstance.get(
        `/meeting/${assistantInviteToken}/get-meeting-by-assistant`
      );
      return data;
    } catch (error) {
      console.error('Error getting meeting by assistant:', error);
      throw new Error('Error getting meeting by assistant');
    }
  }

  public async createRoom(meetingUid: string, room: CreateEditMeetingRoom): Promise<MeetingRoom> {
    try {
      const { data } = await this.axiosInstance.post(`/rooms/${meetingUid}`, {
        name: room.name
      });
      return data;
    } catch (error) {
      console.error('Error creating room:', error);
      throw new Error('Error creating room');
    }
  }

  public async editRoom(
    meetingUid: string,
    roomId: number,
    room: CreateEditMeetingRoom
  ): Promise<boolean> {
    try {
      const { data } = await this.axiosInstance.put(`/rooms/${meetingUid}/${roomId}`, {
        name: room.name
      });
      return data;
    } catch (error) {
      console.error('Error editing room:', error);
      throw new Error('Error editing room');
    }
  }

  public async getRooms(meetingUid: string): Promise<MeetingRoom[]> {
    try {
      const { data } = await this.axiosInstance.get(`/rooms/${meetingUid}`);
      return data;
    } catch (error) {
      console.error('Error getting rooms:', error);
      throw new Error('Error getting rooms');
    }
  }

  public async sendMessage(message: SendMessage): Promise<boolean | undefined> {
    try {
      const { data } = await this.axiosInstance.post('/messages/send-message', message);
      return data;
    } catch (error) {
      console.error('Error sending message', error);
      throw new Error('Error sending message');
    }
  }

  public async getMessages(params: GetMessagesParams): Promise<GetMessages[]> {
    try {
      const { data } = await this.axiosInstance.get('/messages/', { params });
      return data;
    } catch (error) {
      console.error('Error getting messages', error);
      throw new Error('Error getting messages');
    }
  }
}

export default new Api();
